import React from "react";
import { Formik, Field } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    CircularProgress,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from "@material-ui/core";
import * as Yup from "yup";
import { BackButton } from '../../sc-utilities';
import RichTextEditor from './RichTextEditor';
import { exampleValues, capitalize } from "../../sc-utilities";
import { DropzoneArea } from 'material-ui-dropzone'

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    },
    buttons: {
        textAlign: 'right'
    },
    img: {
        width: '100%',
        height: '100%',
        maxWidth: window.innerWidth - 100,
        maxHeight: window.innerHeight - 100
    }
});

const BinaryImageForm = props => {
    const { classes, formSettings, initialValues, loading, handleSubmit, translations, binaryImageType, handleUpload, edit, customers } = props;
    const downloadTypes = [
        { value: 'Internal', displayValue: translations['internal_binary_image'] || 'Internal' },
        { value: 'External', displayValue: translations['external_binary_image'] || 'External' },
        { value: 'External-AzureBlob', displayValue: translations['azure_binary_image'] || 'Azure' }
    ];
    const customerSelect = customers.map(customer => ({ value: customer.CustomerID, displayValue: customer.Name }))
    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, setFieldValue, handleChange, handleBlur, handleSubmit, isSubmitting } = formikProps;

                    const inputProps = {
                        autoComplete: 'new-password',
                        form: {
                            autoComplete: 'off',
                        },
                    }

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                name="MajorVersion"
                                value={values.MajorVersion}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.MajorVersion && touched.MajorVersion)}
                                placeholder={translations["major_version"] || "Major version"}
                                label={translations["major_version"] || "Major version"}
                                margin={"dense"}
                                fullWidth={true}
                                type={"number"}
                            />
                            <TextField
                                name="MinorVersion"
                                value={values.MinorVersion}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.MinorVersion && touched.MinorVersion)}
                                placeholder={translations["minor_version"] || "Minor version"}
                                label={translations["minor_version"] || "Minor version"}
                                margin={"dense"}
                                fullWidth={true}
                                type={"number"}
                            />
                            <TextField
                                name="BuildNumber"
                                value={values.BuildNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.BuildNumber && touched.BuildNumber)}
                                placeholder={translations["build_number"] || "Build number"}
                                label={translations["build_number"] || "Build number"}
                                margin={"dense"}
                                fullWidth={true}
                                type={"number"}
                            />
                            <FormControl id="binary_image_type_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["binary_image_type_select"] || "Please select download type"}</InputLabel>
                                <Select
                                    name="BinaryImageTypeID"
                                    label={translations["binary_image_type"] || "Download type"}
                                    margin={"dense"}
                                    value={values.BinaryImageTypeID || ''}
                                    onChange={handleChange}
                                >
                                    {
                                        (binaryImageType ? [...binaryImageType] : []).map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl id="binary_image_download_type_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["binary_image_download_type_select"] || "Please select type"}</InputLabel>
                                <Select
                                    name="Type"
                                    label={translations["binary_image_download_type"] || "Type"}
                                    margin={"dense"}
                                    value={values.Type || ''}
                                    onChange={handleChange}
                                >
                                    {
                                        (downloadTypes ? [...downloadTypes] : []).map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            {
                                values?.Type?.includes('External') ?
                                    <>
                                        <TextField
                                            name="URL"
                                            value={values.URL}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.URL && touched.URL)}
                                            placeholder={translations["binary_image_url"] || "URL"}
                                            label={translations["binary_image_url"] || "URL"}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="CRC"
                                            value={values.CRC}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.CRC && touched.CRC)}
                                            placeholder={translations["binary_image_crc"] || "CRC (hex number expected)"}
                                            label={translations["binary_image_crc"] || "CRC"}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                    </>
                                    : null
                            }
                            <TextField
                                name="Name"
                                value={values.Name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations["name"] || "Name"}
                                label={translations["name"] || "Name"}
                                margin={"dense"}
                                fullWidth={true}
                                inputProps={inputProps}
                            />
                            <FormControl id="build_type_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["build_type_form_text_helper"] || "Please choose build type"}</InputLabel>
                                <Select
                                    name="BuildType"
                                    value={values.BuildType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.BuildType && touched.BuildType)}
                                    placeholder={translations["build_type"] || "Build type"}
                                    label={translations["build_type"] || "Build type"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value="beta">
                                        {translations["beta"] || "Beta"}
                                    </MenuItem>
                                    <MenuItem value="prod">
                                        {translations["production"] || "Production"}
                                    </MenuItem>
                                    <MenuItem value="internal">
                                        {translations["internal"] || "Internal"}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl id="public_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["is_this_public_image"] || "Is this a download for all customers?"}</InputLabel>
                                <Select
                                    name="Public"
                                    value={values.Public}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Public && touched.Public)}
                                    placeholder={translations["public"] || "Public"}
                                    label={translations["public"] || "Public"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value="yes">
                                        {translations["yes"] || "Yes"}
                                    </MenuItem>
                                    <MenuItem value="no">
                                        {translations["no"] || "No"}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {
                                values?.Public === 'no' ?
                                    <FormControl id="binary_image_customer_id_select" className={classes.formControl} margin={"dense"}>
                                        <InputLabel shrink>{translations["binary_image_customer_id_select"] || "Please select Customer ID"}</InputLabel>
                                        <Select
                                            name="CustomerID"
                                            label={translations["customer_id"] || "Customer ID"}
                                            margin={"dense"}
                                            value={values.CustomerID || ''}
                                            onChange={handleChange}
                                        >
                                            {
                                                (customerSelect ? [...customerSelect] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                    : null
                            }
                            <FormControl id="severity_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["please_select_severity"] || "Please select severity"}</InputLabel>
                                <Select
                                    name="Severity"
                                    value={values.Severity}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Severity && touched.Severity)}
                                    placeholder={translations["severity"] || "Severity"}
                                    label={translations["severity"] || "Severity"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value="0">
                                        Inactive
                                    </MenuItem>
                                    <MenuItem value="1">
                                        Minor
                                    </MenuItem>
                                    <MenuItem value="2">
                                        Normal
                                    </MenuItem>
                                    <MenuItem value="3">
                                        Urgent
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <Grid container spacing={3} >
                                <Grid item xs={9}>
                                    <FormControl id="release_notes_input" className={classes.formControl} margin={"dense"}>
                                        <InputLabel shrink>{translations["release_notes"] || "Release notes"}</InputLabel>
                                        <br />
                                        <Field
                                            value={values.ReleaseNotes}
                                            name="ReleaseNotes"
                                            component={RichTextEditor}
                                            setFieldValue={setFieldValue}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    <br />
                                    <div dangerouslySetInnerHTML={{ __html: exampleValues }} />
                                </Grid>
                            </Grid>
                            {!edit && values?.Type === 'Internal' ? (
                                <div className={classes.img}>
                                    <DropzoneArea
                                        acceptedFiles={["application/*", "text/*", "image/*", "video/*", "font/*", ".run", ".deb", ".iso"]}
                                        filesLimit={1}
                                        maxFileSize={5368709120}
                                        getFileRemovedMessage={(fileName) => {
                                            const text = translations ? (
                                                capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['removed'] || 'removed'}.`)
                                            ) : `File ${fileName} removed.`
                    
                                            return text;
                                        }}
                                        getFileAddedMessage={(fileName) => {
                                            const text = translations ? (
                                                capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['successfully_added'] || 'successfully added'}.`)
                                            ) : `File ${fileName} successfully added.`
                    
                                            return text;
                                        }}
                                        getFileLimitExceedMessage={(filesLimit) => {
                                            const text = translations ? (
                                                capitalize(`${translations['maximum_allowed_number'] || 'Maximum allowed number of files exceeded. Only'} ${filesLimit} ${translations['allowed'] || 'allowed'}.`)
                                            ) : `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`
                    
                                            return text;
                                        }}
                                        getDropRejectMessage={(rejectedFile) => {
                                            const text = translations ? (
                                                capitalize(`${translations['File'] || 'File'} ${translations['was_rejected'] || 'was rejected'}.`)
                                            ) : `File was rejected.`
                    
                                            return text;
                                        }}
                                        dropzoneText={translations['drag_and_drop_file'] || 'Drag and drop file here'}
                                        onChange={handleUpload}
                                    />
                                </div>) : null}
                            <div className={classes.buttons}>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting || (values?.Type === 'Internal' && loading) || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};

export default withStyles(styles, { withTheme: true })(BinaryImageForm);